import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { MoreVertical } from "lucide-react";
import { ReactComponent as AddContainImage } from "../../../../../assets/icons/svg/AddContain_image.svg";
import { ReactComponent as IconAddContain } from "../../../../../assets/icons/svg/Add-contain-icon.svg";
import { ReactComponent as IconPreview } from "../../../../../assets/icons/svg/Preview-Icon.svg";
import { ReactComponent as PenEdit } from "../../../../../assets/icons/svg/pen-icon.svg";
import { usePlaylist } from "../../../../../context/PlaylistContext";
import { useTopBar } from "../../../../../context/TopBarContext";
import { useCreatives } from "../../../../../context/CreativeContext";
import ToastContainer, { useToast } from "../../../../ui/Toast_Message";
import ItemListPopup from "../../../../ui/ItemListPopup";
import CustomButton from "../../../../ui/CommonButton";
import DeletePopup from "../../../../ui/DeletePopup";
import SchedulePlaylistModal from "./SchedulePlaylistModal";
import PlaylistPreviewModal from "./PlaylistPreviewModal";
import PlaylistDetailsPreview from "./PlaylistDetailsPreview";

/**
 * Ensures playlist has a valid imageUrl for components that require it
 */
const ensurePlaylistImageUrl = (playlist: any): any => ({
  ...playlist,
  imageUrl: playlist.imageUrl || ''
});

// Types
interface CreativeWithDuration {
  id: string;
  title: string;
  imageUrl: string;
  fileFormat?: string;
  duration: number; // Required since we always set it
  orientation: "landscape" | "portrait";
  isPlaying: boolean;
  createdOn: string;
  dateRange: string;
  locations: string;
  tags: string[];
  format: string;
  isScheduled?: boolean;
}

interface DragState {
  isDragging: boolean;
  draggedId: string | null;
  draggedIndex: number | null;
  dropTargetIndex: number | null;
}

// Utilities
const formatTotalDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  if (hours > 0) {
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
  }
  return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
    .toString()
    .padStart(2, "0")}`;
};

const getImageFormat = (creative: CreativeWithDuration): string => {
  if (creative.imageUrl?.startsWith("data:")) {
    const match = creative.imageUrl.match(/^data:(image\/[a-zA-Z]+);base64,/);
    return match ? match[1].replace("image/", "") : "jpeg";
  }

  if (creative.fileFormat) {
    return creative.fileFormat.toLowerCase();
  }

  if (creative.imageUrl) {
    const urlParts = creative.imageUrl.split(".");
    const extension = urlParts[urlParts.length - 1].split("?")[0].toLowerCase();
    return extension;
  }

  return "jpeg";
};

// Components
const ImagePlaceholder: React.FC<{ size?: "sm" | "lg" }> = ({
  size = "sm",
}) => (
  <svg
    className={
      size === "lg" ? "w-16 h-16 text-gray-400" : "w-6 h-6 text-gray-400"
    }
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={size === "lg" ? 1.5 : 2}
      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
);

const DurationInput: React.FC<{
  value: number;
  max: number;
  label: string;
  onChange: (value: number) => void;
}> = ({ value, max, label, onChange }) => (
  <div className="flex flex-col items-center">
    <input
      type="number"
      min="0"
      max={max}
      value={value.toString().padStart(2, "0")}
      onChange={(e) =>
        onChange(Math.max(0, Math.min(max, parseInt(e.target.value) || 0)))
      }
      className="w-16 h-12 px-2 py-2 text-center text-lg font-medium border-2 border-gray-200 rounded-lg focus:outline-none focus:border-[#0BA6C7] focus:ring-2 focus:ring-[#0BA6C7]/20 transition-colors"
    />
    <span className="text-xs text-[#9CA3AF] mt-2 font-medium">{label}</span>
  </div>
);

const CreativeEditContent: React.FC<{
  creative: CreativeWithDuration;
  onDurationUpdate: (duration: number) => void;
  onDelete: () => void;
  onClose: () => void;
}> = ({ creative, onDurationUpdate, onDelete, onClose }) => {
  const [imageError, setImageError] = useState(false);
  const [hours, setHours] = useState(
    Math.floor((creative.duration || 10) / 3600)
  );
  const [minutes, setMinutes] = useState(
    Math.floor(((creative.duration || 10) % 3600) / 60)
  );
  const [seconds, setSeconds] = useState((creative.duration || 10) % 60);

  const handleUpdate = () => {
    const totalSeconds = hours * 3600 + minutes * 60 + seconds;
    if (totalSeconds > 0) {
      onDurationUpdate(totalSeconds);
      onClose();
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Creative Preview */}
      <div className="mb-6 flex justify-center">
        <div className="relative inline-block group">
          {!imageError ? (
            <img
              src={creative.imageUrl}
              alt="Creative Preview"
              onError={() => setImageError(true)}
              className="max-h-64 max-w-full rounded shadow-md object-contain"
            />
          ) : (
            <ImagePlaceholder size="lg" />
          )}
        </div>
      </div>

      {/* File Details */}
      <div className="space-y-3 mb-6">
        <div className="flex items-start space-x-2">
          <label className="text-sm font-medium text-[#808191] min-w-fit">
            File name :
          </label>
          <p className="text-sm text-[#1F1F1F] font-normal break-words">
            {creative.title}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-[#808191] min-w-fit">
            File type :
          </label>
          <p className="text-sm text-[#1F1F1F] font-normal">
            image/{getImageFormat(creative)}
          </p>
        </div>
      </div>

      {/* Duration Section */}
      <div className="flex-1">
        <label className="block text-sm font-medium text-[#1F1F1F] mb-4">
          Duration
        </label>
        <div className="flex items-center justify-start space-x-2 mb-6">
          <DurationInput
            value={hours}
            max={23}
            label="hr"
            onChange={setHours}
          />
          <span className="text-2xl font-bold text-[#6B7280] mb-6">:</span>
          <DurationInput
            value={minutes}
            max={59}
            label="min"
            onChange={setMinutes}
          />
          <span className="text-2xl font-bold text-[#6B7280] mb-6">:</span>
          <DurationInput
            value={seconds}
            max={59}
            label="sec"
            onChange={setSeconds}
          />
        </div>

        {/* Delete Button */}
        <div className="flex justify-center mb-6 pt-6 border-t border-gray-200">
          <button
            onClick={onDelete}
            className="flex items-center gap-2 text-[#EF4444] hover:text-[#DC2626] transition-colors font-medium"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
            Delete content
          </button>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mt-auto pt-6 border-t border-gray-200">
        <div className="flex gap-3">
          <CustomButton variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </CustomButton>
          <CustomButton
            variant="default"
            isActive={true}
            onClick={handleUpdate}
            className="flex-1"
          >
            Update
          </CustomButton>
        </div>
      </div>
    </div>
  );
};

const CreativeCard: React.FC<{
  creative: CreativeWithDuration;
  index: number;
  onEdit: () => void;
  dragState: DragState;
  onDragStart: (index: number, creativeId: string) => void;
  onDragEnd: () => void;
  onDragOver: (index: number) => void;
  onDrop: (dropIndex: number) => void;
}> = ({
  creative,
  index,
  onEdit,
  dragState,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
}) => {
  const [imageError, setImageError] = useState(false);

  const isDragging =
    dragState.isDragging && dragState.draggedId === creative.id;
  const isDropTarget = dragState.dropTargetIndex === index;

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.currentTarget.outerHTML);
    onDragStart(index, creative.id);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    onDragOver(index);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    onDrop(index);
  };

  const cardClasses = [
    "flex w-[240px] h-[68px] 4xl:w-[378px] 4xl:h-[98px] items-center bg-white rounded-[8px] transition-all duration-200 relative pointer-events-auto cursor-move",
    isDragging && "opacity-50 scale-105 z-10",
    isDropTarget && "border-2 border-dashed border-[#0BA6C7] bg-blue-50",
  ]
    .filter(Boolean)
    .join(" ");

  const boxShadow = isDragging
    ? "0 8px 32px rgba(11, 166, 199, 0.3)"
    : "0 0 0 1px #F0F3F6, 0 4px 20px rgba(240, 243, 246, 0.8)";

  return (
    <div
      className={cardClasses}
      style={{ boxShadow, backdropFilter: "blur(43.49px)" }}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={onDragEnd}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Creative Image */}
      <div className="w-[60px] h-[68px] 4xl:w-[82px] 4xl:h-[98px] rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0">
        {!imageError ? (
          <img
            src={creative.imageUrl}
            alt="Creative Thumbnail"
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
        ) : (
          <ImagePlaceholder />
        )}
      </div>

      {/* Creative Info */}
      <div className="flex-1 ml-3 min-w-0">
        <div className="flex items-center gap-2 mb-[2px]">
          <h3
            className="text-base font-medium font-inter text-[#11142D] truncate"
            title={creative.title}
          >
            {creative.title}
          </h3>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onEdit();
            }}
            className="p-1 hover:bg-gray-100 rounded transition-colors pointer-events-auto"
            title="Edit creative details"
          >
            <PenEdit className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center text-xs font-normal text-[#3C3C3C]">
          <span>image/{getImageFormat(creative)}</span>
          <span className="mx-2 text-[#80819199]">|</span>
          <span>{creative.duration || 10} s</span>
        </div>
      </div>

      {/* Numbered Badge */}
      <div
        className="absolute -bottom-1.5 right-1 text-3xl 4xl:text-5xl font-bold select-none pointer-events-none transition-colors"
        style={{
          color: "#0BA6C7",
          opacity: isDragging ? 0.8 : 0.2,
        }}
      >
        {index + 1}
      </div>

      {/* Drop indicator */}
      {isDropTarget && (
        <div className="absolute inset-0 rounded-[8px] border-2 border-dashed border-[#0BA6C7] pointer-events-none">
          <div className="absolute inset-0 bg-[#0BA6C7] bg-opacity-10 rounded-[6px]" />
        </div>
      )}
    </div>
  );
};

const EmptyState: React.FC<{
  isNewPlaylist: boolean;
  onAddContent: () => void;
  isNavigating: boolean;
}> = ({ isNewPlaylist, onAddContent, isNavigating }) => (
  <div className="flex-1 flex items-center justify-center p-8">
    <div className="flex flex-col items-center text-center space-y-6 max-w-md">
      <div className="w-52 h-52 -mb-10">
        <AddContainImage className="w-full h-full" />
      </div>
      <div className="space-y-2">
        <h2 className="text-xl 2xl:text-2xl font-semibold text-[#1F1F1F]">
          Add content
        </h2>
        <p className="text-sm text-[#808191F3] leading-relaxed">
          {isNewPlaylist
            ? "Your playlist has been created. Add content to organize the order you want to show. It's as easy as drag-and-drop."
            : "Playlists help you organize the order of the content that you want to show. It's as easy as drag-and-drop."}
        </p>
      </div>
      <CustomButton
        variant="default"
        isActive={true}
        onClick={onAddContent}
        disabled={isNavigating}
        className="mt-4"
      >
        <IconAddContain className="w-5 h-5" />
        <span>{isNavigating ? "Loading..." : "Add content"}</span>
      </CustomButton>
    </div>
  </div>
);

const MoreDropdown: React.FC<{
  isVisible: boolean;
  isPlaylistScheduled: boolean;
  onSchedulePlaylist: () => void;
  onViewDetails: () => void;
  onDeletePlaylist: () => void;
}> = ({ isVisible, isPlaylistScheduled, onSchedulePlaylist, onViewDetails, onDeletePlaylist }) => {
  if (!isVisible) return null;

  return (
    <div
      className="absolute right-0 top-full mt-2 w-40 bg-white rounded-lg py-2 z-50"
      style={{
        boxShadow: "2px 4px 16px 0px #00000026",
        borderRadius: "8px",
      }}
    >
      {isPlaylistScheduled ? (
        <button
          onClick={onViewDetails}
          className="w-full px-3 py-[10px] text-left text-sm text-[#1F1F1F] hover:bg-[#EFFCFF] transition-colors"
        >
          View details
        </button>
      ) : (
        <button
          onClick={onSchedulePlaylist}
          className="w-full px-3 py-[10px] text-left text-sm text-[#1F1F1F] hover:bg-[#EFFCFF] transition-colors"
        >
          Schedule playlist
        </button>
      )}
      <button
        onClick={onDeletePlaylist}
        className="w-full px-3 py-[10px] text-left text-sm text-[#1F1F1F] hover:bg-[#EFFCFF] transition-colors"
      >
        Delete playlist
      </button>
    </div>
  );
};

// Helper function to safely convert Creative to CreativeWithDuration
const convertToCreativeWithDuration = (
  creative: any, 
  duration: number
): CreativeWithDuration => {
  return {
    id: creative.id,
    title: creative.title,
    imageUrl: creative.imageUrl,
    fileFormat: creative.fileFormat,
    duration: duration,
    orientation: creative.orientation,
    isPlaying: creative.isPlaying,
    createdOn: creative.createdOn,
    dateRange: creative.dateRange,
    locations: creative.locations,
    tags: creative.tags,
    format: creative.format,
    isScheduled: creative.isScheduled,
  };
};

// Main Component
const DefaultEmptyStateView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();

  const {
    getPlaylistById,
    updatePlaylist,
    schedulePlaylist,
    deletePlaylist,
    updatePlaylistCreatives,
    getPlaylistWithCreatives,
  } = usePlaylist();

  const { setTitle, setShowBackButton, pushNavState } = useTopBar();
  const { allCreatives } = useCreatives();
  const { toasts, showToast } = useToast();

  // FIXED: Use refs to prevent infinite loops
  const initialLoadRef = useRef(false);
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // State
  const [isNewPlaylist, setIsNewPlaylist] = useState(false);
  const [selectedCreativeIds, setSelectedCreativeIds] = useState<string[]>([]);
  const [deletedCreativeIds, setDeletedCreativeIds] = useState<string[]>([]);
  const [creativeDurations, setCreativeDurations] = useState<
    Record<string, number>
  >({});
  const [hasShownToast, setHasShownToast] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [editingCreative, setEditingCreative] =
    useState<CreativeWithDuration | null>(null);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [creativeToDelete, setCreativeToDelete] = useState<string | null>(null);
  const [showMoreDropdown, setShowMoreDropdown] = useState(false);
  const [showSchedulePopup, setShowSchedulePopup] = useState(false);
  const [isPlaylistScheduled, setIsPlaylistScheduled] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showDeletePlaylistPopup, setShowDeletePlaylistPopup] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Drag and drop state
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    draggedId: null,
    draggedIndex: null,
    dropTargetIndex: null,
  });

  const playlist = id ? getPlaylistById(id) : null;

  // Computed values - Fixed version
  const selectedCreatives = useMemo((): CreativeWithDuration[] => {
    if (!allCreatives || selectedCreativeIds.length === 0) {
      return [];
    }

    const validCreatives: CreativeWithDuration[] = [];
    
    for (const creativeId of selectedCreativeIds) {
      const creative = allCreatives.find(c => c.id === creativeId);
      if (!creative) continue;

      const isDeleted = deletedCreativeIds.includes(creativeId);
      if (isDeleted) continue;

      const duration = creativeDurations[creativeId] || creative.duration || 10;
      const creativeWithDuration = convertToCreativeWithDuration(creative, duration);
      validCreatives.push(creativeWithDuration);
    }

    return validCreatives;
  }, [allCreatives, selectedCreativeIds, deletedCreativeIds, creativeDurations]);

  const totalDuration = useMemo(() => {
    const totalSeconds = selectedCreatives.reduce((sum, creative) => {
      return sum + creative.duration;
    }, 0);
    return formatTotalDuration(totalSeconds);
  }, [selectedCreatives]);

  const shouldShowEmptyState = selectedCreatives.length === 0;
  
  // FIXED: Only consider popups that actually block interactions
  const isAnyPopupOpen = editingCreative !== null || showDeletePopup || showSchedulePopup || showDeletePlaylistPopup || showDetailsModal;

  // Drag and Drop Handlers
  const handleDragStart = useCallback((index: number, creativeId: string) => {
    setDragState({
      isDragging: true,
      draggedId: creativeId,
      draggedIndex: index,
      dropTargetIndex: null,
    });
  }, []);

  const handleDragEnd = useCallback(() => {
    setDragState({
      isDragging: false,
      draggedId: null,
      draggedIndex: null,
      dropTargetIndex: null,
    });
  }, []);

  const handleDragOver = useCallback(
    (index: number) => {
      if (dragState.isDragging && dragState.draggedIndex !== index) {
        setDragState((prev) => ({ ...prev, dropTargetIndex: index }));
      }
    },
    [dragState.isDragging, dragState.draggedIndex]
  );

  const handleDrop = useCallback(
    (dropIndex: number) => {
      const { draggedIndex, draggedId } = dragState;

      if (
        draggedIndex === null ||
        draggedId === null ||
        draggedIndex === dropIndex
      ) {
        handleDragEnd();
        return;
      }

      setSelectedCreativeIds((prev) => {
        const newOrder = [...prev];
        const [removed] = newOrder.splice(draggedIndex, 1);
        newOrder.splice(dropIndex, 0, removed);
        return newOrder;
      });

      showToast(
        "success",
        "Order updated!",
        `Content moved to position ${dropIndex + 1}`,
        `reorder-${draggedId}-${Date.now()}`
      );

      handleDragEnd();
    },
    [dragState, handleDragEnd, showToast]
  );

  const getPlaylistStatus = () => {
    if (
      playlist?.status === "Playing" ||
      playlist?.isScheduled ||
      isPlaylistScheduled
    ) {
      return "Playing";
    } else if (isNewPlaylist && shouldShowEmptyState) {
      return "Not saved yet";
    } else {
      return "Draft saved";
    }
  };

  // FIXED: Consolidated and optimized data loading effect
  useEffect(() => {
    // Prevent infinite loops by checking if we've already loaded
    if (initialLoadRef.current) return;

    // Extract stable values from location state to avoid object reference issues
    const stateCreativeIds = location.state?.selectedCreativeIds;
    const stateNewCreatives = location.state?.newSelectedCreatives;
    const stateCreativeDurations = location.state?.creativeDurations;
    const stateIsNewPlaylist = location.state?.isNewPlaylist;

    console.log("=== LOADING PLAYLIST DATA (ONE TIME) ===");
    console.log("Playlist:", playlist);
    console.log("State creative IDs:", stateCreativeIds);
    console.log("State new creatives:", stateNewCreatives);

    // If we have a playlist ID but no playlist data, wait for it to load
    if (id && !playlist) {
      console.log("Waiting for playlist to load...");
      return;
    }

    let hasLoadedData = false;

    // Priority 1: Load from location state (when coming from content selection)
    if (stateCreativeIds || stateNewCreatives) {
      console.log("Loading from location state");
      const creativeIds = stateNewCreatives || stateCreativeIds || [];
      setSelectedCreativeIds(creativeIds);
      
      if (stateCreativeDurations) {
        setCreativeDurations(stateCreativeDurations);
      }
      
      setIsNewPlaylist(stateIsNewPlaylist || false);
      hasLoadedData = true;
    }
    
    // Priority 2: Load from playlist context (existing playlist)
    else if (playlist) {
      console.log("Loading from playlist context");
      setSelectedCreativeIds(playlist.creativeIds || []);
      setCreativeDurations(playlist.creativeDurations || {});
      setIsNewPlaylist(false);
      hasLoadedData = true;
    }
    
    // Priority 3: New playlist case
    else if (stateIsNewPlaylist) {
      console.log("New playlist detected");
      setIsNewPlaylist(true);
      setSelectedCreativeIds([]);
      setCreativeDurations({});
      hasLoadedData = true;
    }

    if (hasLoadedData) {
      setIsLoaded(true);
      initialLoadRef.current = true;
    }
  }, [playlist?.id, id]); // FIXED: Minimal stable dependencies

  // FIXED: Separate effect for handling toast notifications (runs only once)
  useEffect(() => {
    if (!isLoaded || hasShownToast) return;

    const stateNewCreatives = location.state?.newSelectedCreatives;
    const shouldShowToast = location.state?.showAddedToast;

    if (stateNewCreatives?.length > 0 && shouldShowToast) {
      const toastId = `content-added-${id}-${stateNewCreatives.length}-${Date.now()}`;
      
      showToast(
        "success",
        "Added content!",
        `${stateNewCreatives.length} creative${stateNewCreatives.length > 1 ? "s" : ""} added to playlist`,
        toastId
      );

      setHasShownToast(true);

      // Clear the state to prevent re-showing
      if (location.state) {
        delete location.state.showAddedToast;
      }
    }
  }, [isLoaded, hasShownToast, showToast, id, location.state?.newSelectedCreatives?.length, location.state?.showAddedToast]);

  // FIXED: Debounced playlist update effect
  useEffect(() => {
    if (!playlist || !isLoaded || !initialLoadRef.current) return;

    // Clear any pending updates
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    // Debounce updates to prevent rapid successive calls
    updateTimeoutRef.current = setTimeout(() => {
      const validCreativeIds = selectedCreativeIds.filter(
        (id) => !deletedCreativeIds.includes(id)
      );

      // Only update if there's actually a change
      const currentPlaylistCreativeIds = playlist.creativeIds || [];
      const hasChanged = JSON.stringify(validCreativeIds.sort()) !== JSON.stringify(currentPlaylistCreativeIds.sort());

      if (hasChanged) {
        console.log("=== UPDATING PLAYLIST (DEBOUNCED) ===");
        console.log("Valid creative IDs:", validCreativeIds);

        updatePlaylistCreatives(
          playlist.id,
          validCreativeIds,
          creativeDurations,
          allCreatives || []
        );

        updatePlaylist(playlist.id, {
          creativesCount: validCreativeIds.length,
          status: "Draft Saved" as const,
        });
      }
    }, 300); // 300ms debounce

    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [selectedCreativeIds, deletedCreativeIds, creativeDurations, playlist?.id, isLoaded, updatePlaylistCreatives, updatePlaylist, allCreatives]);

  useEffect(() => {
    if (playlist?.status === "Playing" || playlist?.isScheduled) {
      setIsPlaylistScheduled(true);
    }
  }, [playlist?.status, playlist?.isScheduled]);

  // Handle content addition from other screens
useEffect(() => {
  if (!isLoaded) return;
  
  const incomingCreatives = location.state?.newSelectedCreatives;
  const incomingDurations = location.state?.creativeDurations;
  const shouldShowToast = location.state?.showAddedToast;
  const fromContentSelection = location.state?.fromContentSelection;
  const navigationTimestamp = location.state?.navigationTimestamp;

  // Only process if we have new creatives and this is from content selection
  if (
    !incomingCreatives ||
    !Array.isArray(incomingCreatives) ||
    incomingCreatives.length === 0 ||
    !fromContentSelection
  ) {
    return;
  }

  console.log("=== PROCESSING INCOMING CREATIVES ===");
  console.log("Incoming creatives:", incomingCreatives);
  console.log("Navigation timestamp:", navigationTimestamp);

  // Update selected creatives - use functional update to avoid stale closure
  setSelectedCreativeIds((currentIds) => {
    const validCurrentIds = currentIds.filter(
      (id) => !deletedCreativeIds.includes(id)
    );

    // Check if we actually need to update
    const creativesToAdd = incomingCreatives.filter(
      (creativeId: string) => 
        !deletedCreativeIds.includes(creativeId) &&
        !validCurrentIds.includes(creativeId)
    );

    if (creativesToAdd.length === 0) {
      console.log("No new creatives to add");
      return currentIds; // No change needed
    }

    const newIds = [...validCurrentIds, ...creativesToAdd];
    console.log("Updated creative IDs:", newIds);
    return newIds;
  });

  // Update creative durations if provided
  if (incomingDurations) {
    setCreativeDurations(prev => {
      const hasChanges = Object.keys(incomingDurations).some(
        key => prev[key] !== incomingDurations[key]
      );
      
      if (!hasChanges) {
        return prev; // No change needed
      }

      return {
        ...prev,
        ...incomingDurations
      };
    });
  }

  // Show toast if requested and not already shown
  if (shouldShowToast && !hasShownToast) {
    const newlyAddedCount = incomingCreatives.filter(
      (creativeId: string) =>
        !deletedCreativeIds.includes(creativeId) &&
        !selectedCreativeIds.includes(creativeId)
    ).length;

    if (newlyAddedCount > 0) {
      const toastId = `content-added-${id}-${newlyAddedCount}-${navigationTimestamp}`;

      showToast(
        "success",
        "Added content!",
        `${newlyAddedCount} creative${
          newlyAddedCount > 1 ? "s" : ""
        } added to playlist`,
        toastId
      );

      setHasShownToast(true);
    }
  }

  // Clear the state to prevent re-processing
  if (location.state) {
    delete location.state.newSelectedCreatives;
    delete location.state.creativeDurations;
    delete location.state.showAddedToast;
    delete location.state.fromContentSelection;
    delete location.state.navigationTimestamp;
  }

}, [
  isLoaded,
  location.state?.newSelectedCreatives?.length, // Only length, not the array itself
  location.state?.fromContentSelection,
  location.state?.navigationTimestamp, // Unique timestamp prevents re-processing
  deletedCreativeIds.length, // Only length, not the array itself
  hasShownToast,
  showToast,
  id,
  // Remove selectedCreativeIds from dependencies to prevent infinite loop
]);

  // Reset hasShownToast when navigating away and back - COMPLETELY ISOLATED
  useEffect(() => {
    // Reset toast state on location change after a short delay
    const timeoutId = setTimeout(() => {
      setHasShownToast(false);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [location.pathname]); // FIXED: Use pathname instead of location.key

  useEffect(() => {
    if (playlist?.title) {
      setTitle(playlist.title);
      setShowBackButton(true);
      pushNavState({
        path: location.pathname,
        title: playlist.title,
        showBackButton: true,
      });
    }
  }, [
    playlist?.title,
    location.pathname,
    setTitle,
    setShowBackButton,
    pushNavState,
  ]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (showPreviewModal) {
          setShowPreviewModal(false);
        } else if (showDetailsModal) {
          setShowDetailsModal(false);
        } else if (showDeletePlaylistPopup) {
          setShowDeletePlaylistPopup(false);
        } else if (showDeletePopup) {
          setShowDeletePopup(false);
          setCreativeToDelete(null);
        } else if (editingCreative) {
          setEditingCreative(null);
        } else if (showMoreDropdown) {
          setShowMoreDropdown(false);
        } else if (showSchedulePopup) {
          setShowSchedulePopup(false);
        }
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (showMoreDropdown) {
        const target = e.target as HTMLElement;
        if (!target.closest("[data-more-dropdown]")) {
          setShowMoreDropdown(false);
        }
      }
    };

    document.addEventListener("keydown", handleEscape);
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("click", handleClickOutside);
    };
  }, [showDeletePopup, editingCreative, showMoreDropdown, showSchedulePopup, showPreviewModal, showDeletePlaylistPopup, showDetailsModal]);

  const handleAddContent = useCallback(() => {
    if (isNavigating || !id || !playlist || isAnyPopupOpen) return;

    const targetPath = `/playlists/playlist/${id}/add-content`;
    setShowDeletePopup(false);
    setEditingCreative(null);
    setIsNavigating(true);

    requestAnimationFrame(() => {
      try {
        navigate(targetPath, {
          state: {
            isNewPlaylist: isNewPlaylist,
            currentSelectedCreatives: selectedCreativeIds,
            deletedCreatives: deletedCreativeIds,
            playlistStatus: playlist.status,
          },
          replace: false,
        });
        setTimeout(() => setIsNavigating(false), 500);
      } catch (error) {
        console.error("Navigation error:", error);
        alert("Error: Failed to navigate to content selection.");
        setIsNavigating(false);
      }
    });
  }, [
    id,
    playlist,
    isNewPlaylist,
    selectedCreativeIds,
    deletedCreativeIds,
    isNavigating,
    isAnyPopupOpen,
    navigate,
  ]);

  const handlePreview = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isAnyPopupOpen) {
        if (selectedCreatives.length === 0) {
          showToast(
            "error",
            "No content to preview",
            "Please add content to the playlist before previewing.",
            `no-preview-content-${Date.now()}`
          );
          return;
        }
        setShowPreviewModal(true);
      }
    },
    [isAnyPopupOpen, selectedCreatives.length, showToast]
  );

  const handleMoreOptions = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isAnyPopupOpen) {
        setShowMoreDropdown(!showMoreDropdown);
      }
    },
    [isAnyPopupOpen, showMoreDropdown]
  );

  const handleSchedulePlaylist = useCallback(() => {
    setShowMoreDropdown(false);

    if (selectedCreatives.length === 0) {
      showToast(
        "error",
        "No content",
        "Please add content to the playlist before scheduling.",
        `no-content-${Date.now()}`
      );
      return;
    }

    setShowSchedulePopup(true);
  }, [selectedCreatives.length, showToast]);

  const handleDeletePlaylist = useCallback(() => {
    setShowMoreDropdown(false);
    setShowDeletePlaylistPopup(true);
  }, []);

  const handleViewDetails = useCallback(() => {
    setShowMoreDropdown(false);
    
    // DEBUG: Log the playlist data before opening details
    console.log("=== DEFAULT EMPTY STATE: Opening playlist details ===");
    console.log("Current playlist:", playlist);
    console.log("Playlist scheduleDetails:", playlist?.scheduleDetails);
    console.log("Playlist status:", playlist?.status);
    console.log("Playlist isScheduled:", playlist?.isScheduled);
    
    // Try to get the fresh playlist data
    const freshPlaylist = getPlaylistById(playlist?.id || '');
    console.log("Fresh playlist from context:", freshPlaylist);
    console.log("Fresh playlist scheduleDetails:", freshPlaylist?.scheduleDetails);
    
    setShowDetailsModal(true);
  }, [playlist, getPlaylistById]);

  const handleConfirmDeletePlaylist = useCallback(() => {
    if (!playlist) return;

    try {
      deletePlaylist(playlist.id);
      
      showToast(
        "success",
        "Playlist deleted!",
        `"${playlist.title}" has been permanently deleted`,
        `playlist-deleted-${playlist.id}-${Date.now()}`
      );

      setShowDeletePlaylistPopup(false);
      
      // Navigate back to playlists list
      setTimeout(() => {
        navigate('/playlists', { replace: true });
      }, 500);
      
    } catch (error) {
      console.error("Error deleting playlist:", error);
      showToast(
        "error",
        "Delete failed",
        "Failed to delete playlist. Please try again.",
        `playlist-delete-error-${Date.now()}`
      );
    }
  }, [playlist, deletePlaylist, showToast, navigate]);

  const handleCancelDeletePlaylist = useCallback(() => {
    setShowDeletePlaylistPopup(false);
  }, []);

  const handleCreativeEdit = useCallback((creative: CreativeWithDuration) => {
    setEditingCreative(creative);
  }, []);

  const handleDurationUpdate = useCallback(
    (creativeId: string, duration: number) => {
      setCreativeDurations((prev) => {
        const updated = { ...prev, [creativeId]: duration };

        if (playlist && updatePlaylistCreatives) {
          // Pass allCreatives to updatePlaylistCreatives
          updatePlaylistCreatives(playlist.id, selectedCreativeIds, updated, allCreatives || []);
        }

        return updated;
      });

      showToast(
        "success",
        "Duration updated!",
        `Creative duration set to ${formatTotalDuration(duration)}`,
        `duration-updated-${creativeId}-${Date.now()}`
      );
    },
    [showToast, playlist, selectedCreativeIds, updatePlaylistCreatives, allCreatives]
  );

  const handleCreativeDelete = useCallback((creativeId: string) => {
    setCreativeToDelete(creativeId);
    setShowDeletePopup(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!creativeToDelete) return;

    const creativeToDeleteTitle =
      allCreatives?.find((c) => c.id === creativeToDelete)?.title || "Content";

    setDeletedCreativeIds((prev) => [...prev, creativeToDelete]);

    setSelectedCreativeIds((prev) => {
      const updated = prev.filter((id) => id !== creativeToDelete);

      if (playlist && updatePlaylistCreatives) {
        const updatedDurations = { ...creativeDurations };
        delete updatedDurations[creativeToDelete];
        updatePlaylistCreatives(playlist.id, updated, updatedDurations, allCreatives || []);
      }

      return updated;
    });

    setCreativeDurations((prev) => {
      const { [creativeToDelete]: deleted, ...remaining } = prev;
      return remaining;
    });

    showToast(
      "success",
      "Content deleted!",
      `"${creativeToDeleteTitle}" has been permanently removed from the playlist`,
      `content-deleted-${creativeToDelete}-${Date.now()}`
    );

    setShowDeletePopup(false);
    setCreativeToDelete(null);
    setEditingCreative(null);
  }, [
    creativeToDelete,
    allCreatives,
    showToast,
    playlist,
    updatePlaylistCreatives,
    creativeDurations,
  ]);

  const handleCancelDelete = useCallback(() => {
    setShowDeletePopup(false);
    setCreativeToDelete(null);
  }, []);

  const handleScheduleComplete = useCallback(() => {
    setShowSchedulePopup(false);
    setIsPlaylistScheduled(true);

    // DON'T call schedulePlaylist here - it was already called in the modal!
    // Just show the toast and update local state

    showToast(
      "success",
      "Playlist Scheduled!",
      `"${playlist?.title}" has been successfully scheduled and is now playing`,
      `playlist-scheduled-${playlist?.id}-${Date.now()}`
    );
  }, [showToast, playlist?.title, playlist?.id]);

  const handleCloseSchedulePopup = useCallback(() => {
    setShowSchedulePopup(false);
  }, []);

  const handleClosePreviewModal = useCallback(() => {
    setShowPreviewModal(false);
  }, []);

  const handleCloseDetailsModal = useCallback(() => {
    setShowDetailsModal(false);
  }, []);

  const getCreativeToDeleteTitle = () => {
    if (!creativeToDelete) return "";
    const creative = allCreatives?.find((c) => c.id === creativeToDelete);
    return creative ? creative.title : "this content";
  };

  // Show loading state until data is loaded
  if (!isLoaded || !playlist) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
          <p className="text-gray-500">Loading playlist...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* FIXED: Only show overlay for blocking popups */}
      {isAnyPopupOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40" />
      )}

      {/* Toast Container */}
      <div className="fixed top-4 right-4 z-50 pointer-events-none">
        <div className="pointer-events-auto">
          <ToastContainer toasts={toasts} />
        </div>
      </div>

      {/* Main Content */}
      <div className="h-full flex flex-col bg-white">
        {/* Header */}
        <header className="flex items-center justify-between p-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex flex-col">
              <p className="text-base 2xl:text-lg text-[#1B1D21]">
                <span className="font-medium">
                  {totalDuration} (Total duration)
                </span>
              </p>
              <p className="text-xs font-medium text-[#8D8D8D] mt-1">
                You can drag the content to reorder it
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <span
              className={`text-sm font-medium italic ${
                playlist?.status === "Playing" ||
                playlist?.isScheduled ||
                isPlaylistScheduled
                  ? "text-[#10B981]"
                  : "text-[#808191]"
              }`}
            >
              {getPlaylistStatus()}
            </span>

            <button
              onClick={handlePreview}
              className="flex items-center gap-2 px-4 py-2 h-[32px] md:h-[32px] xl:h-[42px] lg:h-[46px] 2xl:h-[46px] 3xl:h-[52px] text-sm text-[#808191] rounded-lg border-[0.5px] border-[#808191] hover:bg-gray-50 transition-colors active:bg-gray-100"
              type="button"
              disabled={isAnyPopupOpen}
            >
              <IconPreview className="w-5 h-5" />
              <span>Preview</span>
            </button>

            <button
              onClick={handleAddContent}
              disabled={isNavigating || isAnyPopupOpen}
              className={`flex items-center h-[32px] md:h-[32px] xl:h-[42px] lg:h-[46px] 2xl:h-[46px] 3xl:h-[52px] gap-2 px-4 py-2 rounded-[8px] transition-colors font-medium ${
                isNavigating || isAnyPopupOpen
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-[#0091AF] hover:bg-[#007B8A] active:bg-[#006B7A]"
              } text-white`}
              type="button"
            >
              <IconAddContain className="w-5 h-5" />
              <span>{isNavigating ? "Loading..." : "Add content"}</span>
            </button>

            <div className="relative" data-more-dropdown>
              <button
                onClick={handleMoreOptions}
                className="text-[#1F1F1F] hover:bg-gray-100 active:bg-gray-200 p-2 rounded-lg transition-colors"
                type="button"
                disabled={isAnyPopupOpen}
              >
                <MoreVertical className="w-6 h-6" />
              </button>

              <MoreDropdown
                isVisible={showMoreDropdown}
                isPlaylistScheduled={isPlaylistScheduled || playlist?.status === "Playing" || playlist?.isScheduled || false}
                onSchedulePlaylist={handleSchedulePlaylist}
                onViewDetails={handleViewDetails}
                onDeletePlaylist={handleDeletePlaylist}
              />
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto">
          {shouldShowEmptyState ? (
            <EmptyState
              isNewPlaylist={isNewPlaylist}
              onAddContent={handleAddContent}
              isNavigating={isNavigating}
            />
          ) : (
            <div className="px-6 py-4">
              <div className="flex flex-wrap gap-4">
                {selectedCreatives.map((creative, index) => (
                  <CreativeCard
                    key={creative.id}
                    creative={creative}
                    index={index}
                    onEdit={() => handleCreativeEdit(creative)}
                    dragState={dragState}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  />
                ))}
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Preview Modal - FIXED: Now separate from blocking overlays */}
      {showPreviewModal && (
        <PlaylistPreviewModal
          isOpen={showPreviewModal}
          creatives={selectedCreatives}
          onClose={handleClosePreviewModal}
          creativeDurations={creativeDurations}
        />
      )}

      {/* Details Modal */}
      {showDetailsModal && playlist && (
        <PlaylistDetailsPreview
          playlist={(() => {
            // Get the most up-to-date playlist data
            const freshPlaylist = getPlaylistById(playlist.id);
            const playlistToUse = freshPlaylist || playlist;
            
            // Ensure it has an imageUrl
            const playlistWithImage = ensurePlaylistImageUrl(playlistToUse);
            
            // Debug log what we're passing
            console.log("=== PASSING TO PLAYLIST DETAILS PREVIEW ===");
            console.log("Playlist with image:", playlistWithImage);
            console.log("Schedule details:", playlistWithImage.scheduleDetails);
            
            return playlistWithImage;
          })()}
          creatives={selectedCreatives}
          scheduleDetails={(() => {
            const freshPlaylist = getPlaylistById(playlist.id);
            const scheduleDetails = freshPlaylist?.scheduleDetails || playlist?.scheduleDetails;
            console.log("Schedule details being passed as prop:", scheduleDetails);
            return scheduleDetails;
          })()}
          onClose={handleCloseDetailsModal}
        />
      )}

      {/* Edit Creative Popup */}
      {editingCreative && (
        <div
          className={`relative z-50 ${
            showDeletePopup || showSchedulePopup || showDeletePlaylistPopup
              ? "pointer-events-none opacity-85"
              : ""
          }`}
        >
          <ItemListPopup
            title="Edit details"
            onClose={() => setEditingCreative(null)}
          >
            <CreativeEditContent
              creative={editingCreative}
              onDurationUpdate={(duration) =>
                handleDurationUpdate(editingCreative.id, duration)
              }
              onDelete={() => handleCreativeDelete(editingCreative.id)}
              onClose={() => setEditingCreative(null)}
            />
          </ItemListPopup>
        </div>
      )}

      {/* Schedule Playlist Popup */}
      {showSchedulePopup && (
        <div className="relative z-50">
          <ItemListPopup
            title="Schedule playlist"
            onClose={handleCloseSchedulePopup}
          >
            <SchedulePlaylistModal
              playlist={ensurePlaylistImageUrl(playlist)}
              selectedCreatives={selectedCreatives}
              onClose={handleCloseSchedulePopup}
              onScheduleComplete={handleScheduleComplete}
            />
          </ItemListPopup>
        </div>
      )}

      {/* Delete Confirmation Popup */}
      {showDeletePopup && (
        <div className="relative z-[60]">
          <DeletePopup
            title="Delete content"
            message={`Are you sure you want to delete "${getCreativeToDeleteTitle()}" from this playlist?`}
            onCancel={handleCancelDelete}
            onConfirm={handleConfirmDelete}
          />
        </div>
      )}

      {/* Delete Playlist Confirmation Popup */}
      {showDeletePlaylistPopup && (
        <div className="relative z-[60]">
          <DeletePopup
            title="Delete playlist"
            message={`Are you sure you want to delete "${playlist?.title}"? This action cannot be undone.`}
            onCancel={handleCancelDeletePlaylist}
            onConfirm={handleConfirmDeletePlaylist}
          />
        </div>
      )}
    </>
  );
};

export default DefaultEmptyStateView;